import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";

// Visual editor for list sections (values, pillars, stats, faq, press logos, …).
// Infers each item's fields from existing data — no JSON typing required.
function ContentArrayEditor({ items, onChange, testid }) {
  const rows = Array.isArray(items) ? items : [];
  const allStrings = rows.length > 0 && rows.every((r) => typeof r === "string");
  const keys = [];
  rows.forEach((r) => { if (r && typeof r === "object") Object.keys(r).forEach((k) => { if (!keys.includes(k)) keys.push(k); }); });
  const stringMode = allStrings || (rows.length === 0 && keys.length === 0);

  const blank = () => (stringMode ? "" : keys.reduce((a, k) => { a[k] = ""; return a; }, {}));
  const updateStr = (i, v) => { const n = [...rows]; n[i] = v; onChange(n); };
  const updateField = (i, k, v) => { const n = [...rows]; n[i] = { ...(n[i] || {}), [k]: v }; onChange(n); };
  const add = () => onChange([...rows, blank()]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid={testid}>
      {rows.map((row, i) => (
        <div key={i} className="flex gap-3 items-start rounded-xl border border-primary/10 bg-background p-4">
          <div className="flex-1 space-y-3">
            {stringMode ? (
              <input value={row || ""} data-testid={`${testid}-${i}`} onChange={(e) => updateStr(i, e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
            ) : (
              keys.map((k) => {
                const val = row?.[k] ?? "";
                const long = String(val).length > 60;
                return (
                  <div key={k}>
                    <label className="text-xs text-muted-foreground capitalize">{k.replace(/_/g, " ")}</label>
                    {long ? (
                      <textarea rows={2} value={val} data-testid={`${testid}-${i}-${k}`} onChange={(e) => updateField(i, k, e.target.value)}
                        className="w-full mt-0.5 rounded-lg border border-input bg-background px-3 py-2 text-sm" />
                    ) : (
                      <input value={val} data-testid={`${testid}-${i}-${k}`} onChange={(e) => updateField(i, k, e.target.value)}
                        className="w-full mt-0.5 rounded-lg border border-input bg-background px-3 py-2 text-sm" />
                    )}
                  </div>
                );
              })
            )}
          </div>
          <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}
            className="shrink-0 w-9 h-9 rounded-lg text-primary/50 hover:text-destructive hover:bg-destructive/10 flex items-center justify-center"><Trash2 size={15} /></button>
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`${testid}-add`}
        className="flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/20 transition-colors">
        <Plus size={15} /> Add item
      </button>
    </div>
  );
}

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [lang, setLang] = useState("en");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/content").then((r) => setContent(r.data)).catch(() => {}); }, []);

  if (!content) return <p className="text-muted-foreground">Loading…</p>;

  const data = content[lang] || {};

  const setField = (section, key, value) => {
    setContent((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [section]: { ...prev[lang][section], [key]: value } },
    }));
  };
  const setArray = (section, value) => {
    setContent((prev) => ({ ...prev, [lang]: { ...prev[lang], [section]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // parse any array json edits
      const clone = JSON.parse(JSON.stringify(content));
      for (const l of ["en", "fr"]) {
        for (const k of Object.keys(clone[l] || {})) {
          if (typeof clone[l][k] === "string" && (k in {})) {} // noop
        }
      }
      await api.put("/admin/content", { content });
      toast.success("Content saved");
    } catch (e) {
      toast.error("Save failed — check JSON arrays");
    } finally { setSaving(false); }
  };

  const isArray = (v) => Array.isArray(v);

  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-medium text-primary">Site Content</h1>
        <div className="flex items-center gap-3">
          <div className="flex rounded-full border border-primary/20 overflow-hidden">
            {["en", "fr"].map((l) => (
              <button key={l} data-testid={`content-lang-${l}`} onClick={() => setLang(l)} className={`px-4 py-2 text-sm font-semibold ${lang === l ? "bg-primary text-primary-foreground" : "text-primary"}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={save} disabled={saving} data-testid="content-save" className="rounded-full bg-secondary text-secondary-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-60">{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-6">Editing <b className="text-primary">{lang === "en" ? "English" : "Français"}</b>. Switch language to edit the other version — both are saved together.</p>

      <div className="space-y-6">
        {Object.keys(data).map((section) => (
          <div key={section} className="bg-card border border-primary/10 rounded-2xl p-6">
            <h2 className="font-display text-xl font-semibold text-primary capitalize mb-4">{section.replace(/_/g, " ")}</h2>
            {isArray(data[section]) ? (
              <ContentArrayEditor items={data[section]} testid={`content-array-${section}`}
                onChange={(arr) => setArray(section, arr)} />
            ) : typeof data[section] === "object" ? (
              <div className="grid md:grid-cols-2 gap-4">
                {Object.keys(data[section]).map((key) => (
                  <div key={key} className={String(data[section][key]).length > 60 ? "md:col-span-2" : ""}>
                    <label className="text-xs text-muted-foreground">{key.replace(/_/g, " ")}</label>
                    {String(data[section][key]).length > 60 ? (
                      <textarea rows={2} data-testid={`content-${section}-${key}`} value={data[section][key]} onChange={(e) => setField(section, key, e.target.value)} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
                    ) : (
                      <input data-testid={`content-${section}-${key}`} value={data[section][key]} onChange={(e) => setField(section, key, e.target.value)} className="w-full mt-1 rounded-xl border border-input bg-background px-3 py-2 text-sm" />
                    )}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
